import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { Home, FolderOpen, Crown, Settings, Play, Gift } from 'lucide-react';
import { useUserStore } from '@/store/userStore';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { canSeeRewards } from '@/utils/rewardsHelpers';

export function BottomNav() {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const activeOrgId = useWorkspaceStore((s) => s.activeOrgId);
  const showRewards = canSeeRewards(user, activeOrgId);

  type NavItem = {
    path: string;
    icon: typeof Home;
    label: string;
    isUpgrade?: boolean;
    isReward?: boolean;
  };
  const navItems: NavItem[] = [
    { path: '/', icon: Home, label: t('navigation.home') },
    { path: '/files', icon: FolderOpen, label: t('navigation.files') },
    { path: '/examples', icon: Play, label: t('navigation.examples') },
    { path: '/subscription', icon: Crown, label: t('navigation.upgrade'), isUpgrade: true },
    ...(showRewards
      ? [{ path: '/rewards', icon: Gift, label: t('navigation.rewards'), isReward: true } as NavItem]
      : []),
    { path: '/account', icon: Settings, label: t('settings.title') },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 bg-surface/95 backdrop-blur-xl border-t border-glass-border safe-area-bottom">
      <div className="flex items-center justify-around px-2 py-2">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              cn(
                'flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all duration-200 min-w-[64px]',
                isActive
                  ? 'text-primary'
                  : 'text-text-tertiary hover:text-text-secondary',
                item.isUpgrade && !isActive && 'text-orange-500'
              )
            }
          >
            {({ isActive }) => (
              <>
                <div className={cn(
                  'relative p-2 rounded-xl transition-all',
                  isActive && 'bg-primary/15'
                )}>
                  <item.icon className={cn(
                    "w-6 h-6 transition-transform",
                    isActive && "scale-110",
                    item.isUpgrade && !isActive && "text-orange-500"
                  )} />
                  {isActive && (
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />
                  )}
                </div>
                <span className={cn(
                  "text-[10px] font-medium",
                  item.isUpgrade && !isActive && "text-orange-500"
                )}>
                  {item.label}
                </span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
